import { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { getToken } from '../../lib/auth';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SurveyOption {
  id: string;
  text: string;
  sortOrder: number;
}

interface SurveyQuestion {
  id: string;
  text: string;
  type: 'single_choice' | 'multiple_choice' | 'text' | 'rating';
  required?: boolean;
  options: SurveyOption[];
  ratingMax?: number;
}

interface Survey {
  id: string;
  title: string;
  description?: string | null;
  questions: SurveyQuestion[];
  completionPoints?: number | null;
  hasResponded?: boolean;
}

interface RespondResult {
  pointsAwarded?: number | null;
}

interface SurveyBlockProps {
  questionnaireId?: string;
}

// ─── Spinner ─────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
    </svg>
  );
}

// ─── Rating input ─────────────────────────────────────────────────────────────

function RatingInput({
  max,
  value,
  onChange,
}: {
  max: number;
  value: number | null;
  onChange: (v: number) => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const display = hovered ?? value;

  // Stars for ≤10, number buttons otherwise
  if (max <= 10) {
    return (
      <div className="flex gap-1">
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(null)}
            className="text-xl leading-none transition-transform hover:scale-110"
          >
            <span className={n <= (display ?? 0) ? 'text-yellow-400' : 'text-[var(--color-border,#e5e7eb)]'}>
              ★
            </span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
            value === n
              ? 'bg-[var(--color-primary,#4F46E5)] text-white'
              : 'border border-[var(--color-border,#e5e7eb)] text-[var(--color-text,#111)] hover:border-[var(--color-primary,#4F46E5)]'
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

// ─── Block ────────────────────────────────────────────────────────────────────

export function SurveyBlock({ props }: { props: SurveyBlockProps }) {
  const { questionnaireId } = props;
  const isAuthenticated = !!getToken();

  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [pointsAwarded, setPointsAwarded] = useState<number | null>(null);

  // answers: text answers by questionId, selected option ids by questionId
  const [textAnswers, setTextAnswers] = useState<Record<string, string>>({});
  const [choiceAnswers, setChoiceAnswers] = useState<Record<string, string[]>>({});
  const [ratingAnswers, setRatingAnswers] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!questionnaireId || !isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    apiGet<Survey>(`/customer/questionnaires/${questionnaireId}`)
      .then((data) => {
        setSurvey(data);
        if (data.hasResponded) setSubmitted(true);
      })
      .catch((err) => {
        setError((err as Error).message ?? 'Failed to load survey');
      })
      .finally(() => setLoading(false));
  }, [questionnaireId, isAuthenticated]);

  function toggleChoice(questionId: string, optionId: string, multi: boolean) {
    setChoiceAnswers((prev) => {
      const current = prev[questionId] ?? [];
      if (multi) {
        return {
          ...prev,
          [questionId]: current.includes(optionId)
            ? current.filter((id) => id !== optionId)
            : [...current, optionId],
        };
      }
      return { ...prev, [questionId]: [optionId] };
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!survey) return;
    setSubmitting(true);
    setError(null);

    try {
      const answers = survey.questions.map((q) => {
        if (q.type === 'text') {
          return { questionId: q.id, textAnswer: textAnswers[q.id] ?? '' };
        }
        if (q.type === 'rating') {
          return { questionId: q.id, ratingValue: ratingAnswers[q.id] ?? null };
        }
        return { questionId: q.id, optionIds: choiceAnswers[q.id] ?? [] };
      });

      const result = await apiPost<RespondResult>(
        `/customer/questionnaires/${questionnaireId}/respond`,
        { answers },
      );
      setPointsAwarded(result.pointsAwarded ?? null);
      setSubmitted(true);
    } catch (err) {
      const errData = (err as Error & { data?: { error?: string } }).data;
      setError(errData?.error ?? (err instanceof Error ? err.message : 'Failed to submit survey'));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!isAuthenticated) {
    return (
      <div className="py-8 px-4">
        <div className="max-w-lg mx-auto rounded-xl border border-[var(--color-border,#e5e7eb)] bg-[var(--color-bg,#fff)] p-6 text-center shadow-sm">
          <p className="text-sm text-[var(--color-text-muted,#6b7280)] mb-3">
            Please log in to take this survey.
          </p>
          <a
            href="/login"
            className="inline-block px-5 py-2 rounded-lg bg-[var(--color-primary,#4F46E5)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Log in
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="py-8 px-4">
        <div className="max-w-lg mx-auto flex justify-center py-10">
          <svg className="w-5 h-5 animate-spin text-[var(--color-primary,#4F46E5)]" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
          </svg>
        </div>
      </div>
    );
  }

  if (error && !survey) {
    return (
      <div className="py-8 px-4">
        <div className="max-w-lg mx-auto text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          {error}
        </div>
      </div>
    );
  }

  if (!survey) return null;

  // Already completed
  if (submitted) {
    return (
      <div className="py-8 px-4">
        <div className="max-w-lg mx-auto rounded-xl border border-green-200 bg-green-50 px-6 py-8 text-center shadow-sm">
          <svg className="w-10 h-10 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-green-800 font-semibold text-base mb-1">Thank you for completing the survey!</p>
          {pointsAwarded != null && pointsAwarded > 0 && (
            <p className="text-green-700 text-sm">You earned <strong>+{pointsAwarded} points</strong>.</p>
          )}
          {survey.hasResponded && pointsAwarded === null && (
            <p className="text-green-700 text-sm">Already completed.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 px-4">
      <form
        onSubmit={handleSubmit}
        className="max-w-lg mx-auto rounded-xl border border-[var(--color-border,#e5e7eb)] bg-[var(--color-bg,#fff)] p-6 shadow-sm space-y-6"
      >
        <div>
          <h3 className="text-base font-bold text-[var(--color-text,#111)]">{survey.title}</h3>
          {survey.description && (
            <p className="text-sm text-[var(--color-text-muted,#6b7280)] mt-1">{survey.description}</p>
          )}
          {survey.completionPoints != null && survey.completionPoints > 0 && (
            <span className="inline-block mt-2 text-xs font-medium text-[var(--color-primary,#4F46E5)] bg-[var(--color-primary,#4F46E5)]/10 rounded-full px-2.5 py-0.5">
              +{survey.completionPoints} points on completion
            </span>
          )}
        </div>

        {survey.questions.map((q, idx) => (
          <div key={q.id} className="space-y-2">
            <label className="block text-sm font-semibold text-[var(--color-text,#111)]">
              {idx + 1}. {q.text}
              {q.required && <span className="text-red-500 ml-0.5">*</span>}
            </label>

            {q.type === 'text' && (
              <textarea
                required={q.required}
                rows={3}
                className="w-full rounded-lg border border-[var(--color-border,#e5e7eb)] px-3 py-2 text-sm text-[var(--color-text,#111)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary,#4F46E5)] resize-none"
                value={textAnswers[q.id] ?? ''}
                onChange={(e) => setTextAnswers((p) => ({ ...p, [q.id]: e.target.value }))}
              />
            )}

            {q.type === 'rating' && (
              <RatingInput
                max={q.ratingMax ?? 5}
                value={ratingAnswers[q.id] ?? null}
                onChange={(v) => setRatingAnswers((p) => ({ ...p, [q.id]: v }))}
              />
            )}

            {(q.type === 'single_choice' || q.type === 'multiple_choice') && (
              <div className="space-y-2">
                {q.options.map((opt) => {
                  const isMulti = q.type === 'multiple_choice';
                  const selected = (choiceAnswers[q.id] ?? []).includes(opt.id);
                  const InputEl = isMulti ? 'checkbox' : 'radio';
                  return (
                    <label
                      key={opt.id}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
                        selected
                          ? 'border-[var(--color-primary,#4F46E5)] bg-[var(--color-primary,#4F46E5)]/10 text-[var(--color-primary,#4F46E5)]'
                          : 'border-[var(--color-border,#e5e7eb)] text-[var(--color-text,#111)] hover:border-[var(--color-primary,#4F46E5)]'
                      }`}
                    >
                      <input
                        type={InputEl}
                        name={`q-${q.id}`}
                        value={opt.id}
                        checked={selected}
                        required={q.required && !isMulti}
                        onChange={() => toggleChoice(q.id, opt.id, isMulti)}
                        className="sr-only"
                      />
                      <span
                        className={`w-4 h-4 flex-shrink-0 flex items-center justify-center rounded-${
                          isMulti ? 'sm' : 'full'
                        } border transition-colors ${
                          selected
                            ? 'border-[var(--color-primary,#4F46E5)] bg-[var(--color-primary,#4F46E5)]'
                            : 'border-[var(--color-border,#d1d5db)]'
                        }`}
                      >
                        {selected && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      {opt.text}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-2.5 rounded-lg bg-[var(--color-primary,#4F46E5)] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting && <Spinner />}
          Submit Survey
        </button>
      </form>
    </div>
  );
}
